import { EstadoBolsa } from './state/bolsaMachine';

export interface BolsaCentro {
  id: string;
  nombre: string;
  estado: string;
  estado_codigo: EstadoBolsa;
  total: number;
  lineas_count: number;
  unidades_count: number;
  createdAt: string;
  updatedAt: string;
  cliente_id?: string | null;
  cliente_nit?: string | null;
  cliente_razon_social?: string | null;
  codigo_bolsa?: string | null;
  codigo_cotizacion?: string | null;
  validez?: string | null;
  motivo_perdida?: string | null;
  motivo_anulacion?: string | null;
  // SPEC-112 — consecutivo PED-NNNN, asignado solo al marcar GANADA
  numero_pedido?: string | null;
}

// SPEC-206 (RN-011, design §7.3) — cambios de una versión frente a la anterior. Con una versión
// anterior a la spec solo viene `total` (detalle_disponible: false en la versión).
export interface CambiosVersion {
  agregadas?: Array<{ linea_id: string; nombre_producto: string; cantidad: number; precio_venta: number }>;
  quitadas?: Array<{ linea_id: string; nombre_producto: string }>;
  cantidad?: Array<{ linea_id: string; nombre_producto: string; anterior: number; nuevo: number }>;
  precio?: Array<{ linea_id: string; nombre_producto: string; anterior: number; nuevo: number }>;
  marcacion?: Array<{ linea_id: string; nombre_producto: string; anterior: string | null; nuevo: string | null }>;
  total: { anterior: number; nuevo: number; diferencia: number; variacion_pct: number | null };
}

// SPEC-206 (design §7.3) — versión enviada tal como la devuelve GET /centro/:id.
export interface VersionCentro {
  id: string;
  version: number;
  estado: 'enviada';
  es_vigente: boolean;
  enviada_en: string | null;
  enviado_a: string;
  validez: string | null;
  total: number;
  pdf_disponible: boolean;
  detalle_disponible: boolean;
  cambios: CambiosVersion | null;
  createdAt: string;
}

// SPEC-206 (RN-007, design §7.1) — resumen del re-precio al reabrir o duplicar.
export interface ReprecioResumen {
  total_anterior: number;
  total_nuevo: number;
  diferencia: number;
  lineas_actualizadas: Array<{
    linea_id: string;
    nombre_producto: string;
    precio_venta_anterior: number;
    precio_venta_nuevo: number;
    variacion_pct: number | null;
  }>;
  sin_precio_vigente: Array<{ linea_id: string; nombre_producto: string }>;
  marcacion_conservada: Array<{
    linea_id: string;
    nombre_producto: string;
    motivo: 'ESQUEMA_NO_RECALCULABLE' | 'SIN_OPCION_APLICABLE' | 'OPCION_INEXISTENTE';
  }>;
}

// SPEC-206 — respuesta de transicionar (reprecio null si no re-precia) y de duplicar.
export interface BolsaConReprecio {
  bolsa: BolsaCentro;
  reprecio: ReprecioResumen | null;
}

export interface BolsaTransicionCentro {
  id: string;
  from_estado: EstadoBolsa | null;
  to_estado: EstadoBolsa;
  actor: string;
  nota: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// SPEC-103 — línea de bolsa/cotización mostrada en el drawer (sección PRODUCTOS)
export interface LineaBolsa {
  id: string;
  nombre_producto?: string;
  imagen_url?: string | null;
  color_seleccionado?: string | null;
  tipo_marcacion_id?: number | null;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

export interface DetalleCentro {
  bolsa: BolsaCentro;
  lineas: LineaBolsa[];
  versiones: VersionCentro[];
  transiciones: BolsaTransicionCentro[];
  creado_en: string;
  // SPEC-206 (CA-016) — hay una versión pendiente: el último intento de envío no llegó a nadie.
  envio_fallido?: { intentado_en: string; version: number } | null;
  // SPEC-206 (CA-033) — cotización de la que esta es copia.
  duplicada_de?: { id: string; codigo_cotizacion: string | null } | null;
  // SPEC-206 (RN-002) — número que tendrá el próximo envío.
  siguiente_version?: number;
}

export interface MetricasCentro {
  pipeline_activo: number;
  ganado: number;
  conversion: number;
  por_vencer: number;
  por_estado: Record<EstadoBolsa, { count: number; total: number }>;
}

export interface ListarCentroResultado {
  items: BolsaCentro[];
  total: number;
  page: number;
  limit: number;
}

export interface ErrorTransicionInvalida {
  code: 'TRANSICION_INVALIDA';
  message: string;
  from: EstadoBolsa;
  to: EstadoBolsa;
  allowed: EstadoBolsa[];
}

// SPEC-127 — detalle por destinatario en el error ENVIO_PARCIAL_RECHAZADO (todo-o-nada)
export interface DetalleDestinatarioError {
  contacto_id: string | null;
  correo: string;
  estado: string;
  error?: string;
}

export interface ApiErrorBody {
  success: false;
  error: {
    code: string;
    message: string;
    from?: EstadoBolsa;
    to?: EstadoBolsa;
    allowed?: EstadoBolsa[];
    detalle?: DetalleDestinatarioError[];
    // SPEC-206 (design §7.2) — en ENVIO_PARCIAL_RECHAZADO: el cliente ya recibió la versión.
    version_consolidada?: boolean;
  };
}

export class CentroApiError extends Error {
  code: string;
  from?: EstadoBolsa;
  to?: EstadoBolsa;
  allowed?: EstadoBolsa[];
  detalle?: DetalleDestinatarioError[];
  version_consolidada?: boolean;

  constructor(body: ApiErrorBody['error']) {
    super(body.message);
    this.code = body.code;
    this.from = body.from;
    this.to = body.to;
    this.allowed = body.allowed;
    this.detalle = body.detalle;
    this.version_consolidada = body.version_consolidada;
  }
}
